/**
 * Incident / Near-miss Log entity DTOs (RIDDOR).
 *
 * Mirrors Prisma `IncidentLog` (prisma/schema.prisma:918). The
 * `isRiddorFlag` boolean drives the "this should be reported to HSE"
 * affordance on the mobile detail screen; the server computes it from the
 * incident type + severity but the client respects the persisted value.
 *
 * Ids are cuid() per the Prisma model.
 */
import { z } from "zod";
import { entityIdSchema } from "../common";
import { CursorPaginatedResponse } from "../pagination";

export const INCIDENT_TYPES = [
  "INJURY",
  "NEAR_MISS",
  "DANGEROUS_OCCURRENCE",
  "DAMAGE",
] as const;

export type IncidentType = (typeof INCIDENT_TYPES)[number];

export const IncidentTypeSchema = z.enum(INCIDENT_TYPES);

export const INCIDENT_SEVERITIES = [
  "MINOR",
  "MODERATE",
  "MAJOR",
  "FATAL",
] as const;

export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

export const IncidentSeveritySchema = z.enum(INCIDENT_SEVERITIES);

export const IncidentSchema = z
  .object({
    id: entityIdSchema,
    projectId: entityIdSchema,
    // Recorder provenance, not ownership — null once the author deleted their
    // account (IncidentLog.userId is SetNull so evidence outlives authors).
    userId: z.string().nullable(),
    occurredAt: z.string().datetime(),
    location: z.string(),
    type: IncidentTypeSchema,
    severity: IncidentSeveritySchema,
    peopleInvolved: z.string(),
    description: z.string(),
    actionsTaken: z.string().nullable(),
    isRiddorFlag: z.boolean(),
    riddorReason: z.string().nullable(),
    photoUrls: z.array(z.string()),
    reportedToHse: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  });

export type Incident = z.infer<typeof IncidentSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the routes serialise through the Strict form.
export const IncidentStrict = IncidentSchema.strict();

/** Compatible mobile list envelope: required fields stay validated while
 * additive server fields are ignored at both envelope and item boundaries. */
export const IncidentListResponseSchema = CursorPaginatedResponse(IncidentSchema.strip());

export const IncidentCreateRequest = z
  .object({
    occurredAt: z.string().datetime(),
    location: z.string().min(1),
    type: IncidentTypeSchema,
    severity: IncidentSeveritySchema,
    peopleInvolved: z.string().min(1),
    description: z.string().min(1),
    actionsTaken: z.string().nullable().optional(),
    riddorReason: z.string().nullable().optional(),
    photoUrls: z.array(z.string()).default([]),
  })
  .strict();

export type IncidentCreateRequest = z.infer<typeof IncidentCreateRequest>;

/**
 * `expectedUpdatedAt` is the OPTIONAL optimistic-concurrency claim (#2369):
 * when present, the server only applies the edit if the incident row's
 * `updatedAt` still equals it (409 CONFLICT otherwise, because the RIDDOR
 * recompute ran against a version someone else already replaced). Absent, the
 * edit behaves exactly as before. A claim, never a written field.
 */
export const IncidentUpdateRequest = IncidentCreateRequest.partial().extend({
  reportedToHse: z.boolean().optional(),
  expectedUpdatedAt: z.string().datetime().optional(),
});

export type IncidentUpdateRequest = z.infer<typeof IncidentUpdateRequest>;
